import { Activity, Check, FlaskConical, Pencil, Server, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import { GlobalAnalysisLimitsCard } from '@/components/AnalysisLimits/GlobalAnalysisLimitsCard';
import forgeService from '@/services/forgeService';
import tokenEconomyService, {
  type OrganizationQuotaListResponse,
  type OrganizationQuotaRow,
} from '@/services/tokenEconomyService';

function pickQuotaColor(pct: number): string {
  if (pct >= 100) return 'hsl(0, 80%, 55%)';
  if (pct >= 90) return 'hsl(0, 75%, 55%)';
  if (pct >= 60) return 'hsl(35, 90%, 50%)';
  return 'hsl(140, 60%, 40%)';
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return new Intl.NumberFormat().format(n);
}

const TokenEconomyPanel: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<OrganizationQuotaListResponse | null>(null);
  const [forgeClientNames, setForgeClientNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingDefault, setEditingDefault] = useState(false);
  const [defaultDraft, setDefaultDraft] = useState('');
  const [savingDefault, setSavingDefault] = useState(false);

  const [demoQuota, setDemoQuota] = useState<number | null>(null);
  const [editingDemo, setEditingDemo] = useState(false);
  const [demoDraft, setDemoDraft] = useState('');
  const [savingDemo, setSavingDemo] = useState(false);

  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  const [orgDraft, setOrgDraft] = useState('');
  const [savingOrgId, setSavingOrgId] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [quotas, systemDefault, clients] = await Promise.all([
        tokenEconomyService.listOrganizationQuotas(),
        tokenEconomyService.getSystemDefault(),
        forgeService.listClients().catch(() => []),
      ]);
      setData(quotas);
      setDemoQuota(systemDefault.demo_token_quota_monthly);
      const clientMap: Record<string, string> = {};
      for (const c of clients) clientMap[c.id] = c.name;
      setForgeClientNames(clientMap);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to load quotas';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const startEditDefault = () => {
    if (!data) return;
    setDefaultDraft(String(data.system_default));
    setEditingDefault(true);
  };

  const cancelEditDefault = () => {
    setEditingDefault(false);
    setDefaultDraft('');
  };

  const saveDefault = async () => {
    const value = parseInt(defaultDraft, 10);
    if (Number.isNaN(value) || value < 0) return;
    setSavingDefault(true);
    try {
      const resp = await tokenEconomyService.setSystemDefault(value);
      setData((prev) =>
        prev ? { ...prev, system_default: resp.genie_artifact_default_token_quota_monthly } : prev,
      );
      setEditingDefault(false);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to update default';
      setError(message);
    } finally {
      setSavingDefault(false);
    }
  };

  const startEditDemo = () => {
    setDemoDraft(String(demoQuota ?? 50000));
    setEditingDemo(true);
  };

  const cancelEditDemo = () => {
    setEditingDemo(false);
    setDemoDraft('');
  };

  const saveDemo = async () => {
    const value = parseInt(demoDraft, 10);
    if (Number.isNaN(value) || value < 0) return;
    setSavingDemo(true);
    try {
      const resp = await tokenEconomyService.setSystemDefault(data!.system_default, value);
      setDemoQuota(resp.demo_token_quota_monthly);
      setEditingDemo(false);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to update demo quota';
      setError(message);
    } finally {
      setSavingDemo(false);
    }
  };

  const startEditOrg = (row: OrganizationQuotaRow) => {
    setOrgDraft(row.quota_override !== null ? String(row.quota_override) : '');
    setEditingOrgId(row.organization_id);
  };

  const cancelEditOrg = () => {
    setEditingOrgId(null);
    setOrgDraft('');
  };

  const saveOrg = async (orgId: string) => {
    const trimmed = orgDraft.trim();
    let value: number | null;
    if (trimmed === '') {
      value = null;
    } else {
      const parsed = parseInt(trimmed, 10);
      if (Number.isNaN(parsed) || parsed < 0) return;
      value = parsed;
    }
    setSavingOrgId(orgId);
    try {
      const updated = await tokenEconomyService.setOrganizationQuota(orgId, value);
      setData((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((row) => (row.organization_id === orgId ? updated : row)),
            }
          : prev,
      );
      setEditingOrgId(null);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to update quota';
      setError(message);
    } finally {
      setSavingOrgId(null);
    }
  };

  const periodLabel = useMemo(() => {
    if (!data) return null;
    const start = new Date(data.period_start);
    return start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }, [data]);

  if (loading && !data) {
    return <p className="text-muted-foreground py-4">{t('common.loading', 'Loading…')}</p>;
  }

  if (!data) {
    return (
      <p className="text-destructive py-4">
        {error ?? t('admin.tokenEconomy.loadError', 'Could not load quotas')}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-md bg-destructive/10 text-destructive px-4 py-2 text-sm">{error}</div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            {t('admin.tokenEconomy.systemDefaultTitle', 'Default monthly quota (GenieArtifact)')}
          </CardTitle>
          <CardDescription>
            {t(
              'admin.tokenEconomy.systemDefaultDescription',
              'Applied to every organization without an explicit per-org override.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {editingDefault ? (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                step={1000}
                value={defaultDraft}
                onChange={(e) => setDefaultDraft(e.target.value)}
                className="max-w-xs"
              />
              <Button size="sm" onClick={saveDefault} disabled={savingDefault}>
                <Check className="h-4 w-4" />
                {t('global.save', 'Save')}
              </Button>
              <Button size="sm" variant="outline" onClick={cancelEditDefault} disabled={savingDefault}>
                <X className="h-4 w-4" />
                {t('global.cancel', 'Cancel')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-2xl font-semibold">{formatCompact(data.system_default)}</span>
              <span className="text-sm text-muted-foreground">
                {t('admin.tokenEconomy.tokensPerMonth', 'tokens / month')}
              </span>
              <Button size="sm" variant="outline" onClick={startEditDefault}>
                <Pencil className="h-4 w-4 mr-1" />
                {t('global.edit', 'Edit')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-orange-500" />
            {t('admin.tokenEconomy.demoQuotaTitle', 'Demo monthly quota')}
          </CardTitle>
          <CardDescription>
            {t(
              'admin.tokenEconomy.demoQuotaDescription',
              'Quota assigned to each new demo organisation on session creation. Separate from the platform default.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {editingDemo ? (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                step={1000}
                value={demoDraft}
                onChange={(e) => setDemoDraft(e.target.value)}
                className="max-w-xs"
              />
              <Button size="sm" onClick={saveDemo} disabled={savingDemo}>
                <Check className="h-4 w-4" />
                {t('global.save', 'Save')}
              </Button>
              <Button size="sm" variant="outline" onClick={cancelEditDemo} disabled={savingDemo}>
                <X className="h-4 w-4" />
                {t('global.cancel', 'Cancel')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-2xl font-semibold">
                {demoQuota !== null ? formatCompact(demoQuota) : '…'}
              </span>
              <span className="text-sm text-muted-foreground">
                {t('admin.tokenEconomy.tokensPerMonth', 'tokens / month')}
              </span>
              <Button size="sm" variant="outline" onClick={startEditDemo}>
                <Pencil className="h-4 w-4 mr-1" />
                {t('global.edit', 'Edit')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t('admin.tokenEconomy.perOrgTitle', 'Per-organization quotas')}
          </CardTitle>
          <CardDescription>
            {periodLabel
              ? t('admin.tokenEconomy.perOrgDescription', {
                  defaultValue: 'Current period: {{period}}. Leave the override empty to inherit the default.',
                  period: periodLabel,
                })
              : t(
                  'admin.tokenEconomy.perOrgDescriptionShort',
                  'Leave the override empty to inherit the default.',
                )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.items.length === 0 ? (
            <p className="text-muted-foreground py-4">
              {t('admin.tokenEconomy.noOrganizations', 'No organizations yet.')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">
                      {t('admin.tokenEconomy.colOrganization', 'Organization')}
                    </th>
                    <th className="py-2 pr-4 font-medium">
                      {t('admin.tokenEconomy.colForgeClient', 'Forge client')}
                    </th>
                    <th className="py-2 pr-4 font-medium">
                      {t('admin.tokenEconomy.colOverride', 'Override')}
                    </th>
                    <th className="py-2 pr-4 font-medium">
                      {t('admin.tokenEconomy.colEffective', 'Effective quota')}
                    </th>
                    <th className="py-2 pr-4 font-medium">
                      {t('admin.tokenEconomy.colUsed', 'Used (this month)')}
                    </th>
                    <th className="py-2 pr-4 font-medium text-right">
                      {t('admin.tokenEconomy.colActions', 'Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => {
                    const isEditing = editingOrgId === row.organization_id;
                    const color = pickQuotaColor(row.percentage);
                    const pct = Math.min(row.percentage, 100);
                    return (
                      <tr key={row.organization_id} className="border-b border-border last:border-0">
                        <td className="py-3 pr-4 font-medium">{row.organization_name}</td>
                        <td className="py-3 pr-4 text-muted-foreground">
                          {row.forge_client_id ? (
                            <span className="inline-flex items-center gap-1">
                              <Server className="h-3 w-3" />
                              {forgeClientNames[row.forge_client_id] ?? row.forge_client_id}
                            </span>
                          ) : (
                            <span className="italic">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          {isEditing ? (
                            <Input
                              type="number"
                              min={0}
                              step={1000}
                              placeholder={t('admin.tokenEconomy.inheritsPlaceholder', 'inherits default')}
                              value={orgDraft}
                              onChange={(e) => setOrgDraft(e.target.value)}
                              className="max-w-[160px]"
                            />
                          ) : row.quota_override !== null ? (
                            <span className="font-semibold">{formatCompact(row.quota_override)}</span>
                          ) : (
                            <span className="italic text-muted-foreground">
                              {t('admin.tokenEconomy.inheritsDefault', 'default')}
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4">{formatCompact(row.effective_quota)}</td>
                        <td className="py-3 pr-4">
                          <div className="flex flex-col gap-1 min-w-[140px]">
                            <div className="flex items-center justify-between text-xs">
                              <span>{formatCompact(row.used)}</span>
                              <span className="font-semibold" style={{ color }}>
                                {Math.round(row.percentage)}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full"
                                style={{ width: `${pct}%`, background: color }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-right">
                          {isEditing ? (
                            <div className="inline-flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => saveOrg(row.organization_id)}
                                disabled={savingOrgId === row.organization_id}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEditOrg}
                                disabled={savingOrgId === row.organization_id}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => startEditOrg(row)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project analysis limits: global values (per-org overrides live on each organization's page). */}
      <GlobalAnalysisLimitsCard />
    </div>
  );
};

export default TokenEconomyPanel;
